import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaHistory, FaBroadcastTower } from "react-icons/fa";
import { FaGift, FaUsers } from "react-icons/fa6";
import { IoIosCreate } from "react-icons/io";
import { RiUserAddFill } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { setActivePage } from "../store/activePage";

const OP_ITEMS = [
  {
    to: "/dashboard/create-new-event",
    icon: IoIosCreate,
    title: "Create New Event",
    desc: "Set up a new moi event with full configuration and guest details",
    accent: "#5eead4",
    glow: "rgba(94,234,212,0.16)",
    border: "rgba(94,234,212,0.32)",
    tag: "Events",
    featured: true,
  },
  {
    to: "/dashboard/live-tracker",
    icon: FaBroadcastTower,
    title: "Live Tracker",
    desc: "Monitor cash collections and operator desk activities in real time",
    accent: "#f43f5e",
    glow: "rgba(244,63,94,0.13)",
    border: "rgba(244,63,94,0.24)",
    tag: "Live",
  },
  {
    to: "/dashboard/upcoming-events",
    icon: FaCalendarAlt,
    title: "All Open Events",
    desc: "View and manage all currently active events",
    accent: "#0f766e",
    glow: "rgba(15,118,110,0.12)",
    border: "rgba(15,118,110,0.24)",
    tag: "Active",
  },
  {
    to: "/dashboard/events-history",
    icon: FaHistory,
    title: "Closed Events",
    desc: "Browse completed events and their contribution records",
    accent: "#334155",
    glow: "rgba(51,65,85,0.12)",
    border: "rgba(51,65,85,0.2)",
    tag: "History",
  },
  {
    to: "/dashboard/customers-history",
    icon: FaUsers,
    title: "Customer List",
    desc: "View all registered customers and their event activity",
    accent: "#0284c7",
    glow: "rgba(2,132,199,0.13)",
    border: "rgba(2,132,199,0.24)",
    tag: "Customers",
  },
  {
    to: "/dashboard/add-customer",
    icon: RiUserAddFill,
    title: "Add Customer",
    desc: "Register a new customer to the platform",
    accent: "#6366f1",
    glow: "rgba(99,102,241,0.13)",
    border: "rgba(99,102,241,0.25)",
    tag: "Onboard",
  },
  {
    to: "/dashboard/rewards-referrals",
    icon: FaGift,
    title: "Rewards & Referrals",
    desc: "Manage referral bonuses, rewards, and loyalty points",
    accent: "#14b8a6",
    glow: "rgba(20,184,166,0.14)",
    border: "rgba(20,184,166,0.24)",
    tag: "Rewards",
  },
];

function OpCard({ item, index, hero }) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  const isFeatured = Boolean(item.featured);

  return (
    <Link
      to={item.to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        animationDelay: `${index * 90}ms`,
        animation: "opCardRise 0.5s cubic-bezier(0.22,1,0.36,1) both",
        border: `1.5px solid ${
          hovered
            ? item.border
            : isFeatured
              ? `${item.border}99`
              : "rgba(255,255,255,0.08)"
        }`,
        boxShadow: hovered
          ? isFeatured
            ? "0 18px 40px rgba(15,39,66,0.18), 0 10px 24px rgba(15,118,110,0.16)"
            : `0 16px 34px ${item.glow}, 0 8px 20px rgba(15,23,42,0.08)`
          : isFeatured
            ? "0 14px 34px rgba(15,39,66,0.14), 0 6px 16px rgba(15,118,110,0.12)"
            : "0 8px 24px rgba(15,23,42,0.05)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        background: isFeatured
          ? "linear-gradient(135deg, #0f2742 0%, #164e63 48%, #0f766e 100%)"
          : "rgba(255,255,255,0.92)",
      }}
      className={`relative flex overflow-hidden rounded-2xl no-underline backdrop-blur-sm ${
        hero ? "flex-row items-center gap-8 p-7 sm:p-8" : "flex-col gap-4 p-5"
      }`}
    >
      {isFeatured && (
        <div
          style={{
            background: `linear-gradient(90deg, transparent, ${item.accent}55, transparent)`,
          }}
          className="pointer-events-none absolute left-0 top-0 h-px w-full"
        />
      )}

      <div
        style={{
          background: item.glow,
          filter: "blur(32px)",
          opacity: hovered ? 1 : isFeatured ? 0.7 : 0.35,
          transition: "opacity 0.3s ease",
          top: hero ? "50%" : "12px",
          left: hero ? "-20px" : "12px",
          transform: hero ? "translateY(-50%)" : "none",
          width: hero ? "180px" : "72px",
          height: hero ? "180px" : "72px",
        }}
        className="pointer-events-none absolute rounded-full"
      />

      <div
        style={{
          color: item.accent,
          filter: hovered
            ? `drop-shadow(0 0 14px ${item.accent})`
            : isFeatured
              ? `drop-shadow(0 0 7px ${item.accent}99)`
              : "none",
          transition: "filter 0.3s ease",
          fontSize: hero ? "3.5rem" : "2.5rem",
          flexShrink: 0,
        }}
      >
        <Icon />
      </div>

      <div className={`flex flex-col ${hero ? "min-w-0 flex-1" : ""}`}>
        <div
          className={`flex items-center gap-2 ${hero ? "mb-3" : "order-first mb-1.5"}`}
        >
          <span
            style={{
              color: item.accent,
              borderColor: item.border,
              background: item.glow,
            }}
            className="rounded-full border px-2.5 py-0.5 text-xs font-black uppercase tracking-widest"
          >
            {item.tag}
          </span>
          {isFeatured && (
            <span className="rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-xs font-bold text-cyan-50">
              Primary
            </span>
          )}
        </div>

        <h2
          style={{ fontSize: hero ? "1.65rem" : "1.05rem" }}
          className={`mb-1.5 font-black leading-tight tracking-tight ${
            isFeatured ? "text-white" : "text-slate-900"
          }`}
        >
          {item.title}
        </h2>
        <p
          className={`leading-relaxed ${
            isFeatured ? "text-slate-200" : "text-slate-500"
          } ${hero ? "max-w-lg text-sm" : "text-xs"}`}
        >
          {item.desc}
        </p>

        <div
          style={{
            color: item.accent,
            opacity: hovered || hero ? 1 : 0,
            transform: hovered ? "translateX(4px)" : "translateX(0)",
            transition: "all 0.25s ease",
          }}
          className={`flex items-center gap-1.5 font-bold ${
            hero ? "mt-4 text-sm" : "mt-2.5 text-xs"
          }`}
        >
          {hero ? "Create Event" : "Open"}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </div>
      </div>

      <div
        style={{
          background: `linear-gradient(90deg, ${item.accent}, transparent)`,
          opacity: hovered ? 1 : isFeatured ? 0.4 : 0,
          transition: "opacity 0.3s ease",
        }}
        className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-full"
      />
    </Link>
  );
}

function OperatorHome() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setActivePage({ headerName: "Home page", activeMenu: "home" }));
  }, [dispatch]);

  const orgName = localStorage.getItem("org-name") || null;

  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? "Good morning"
      : now.getHours() < 17
        ? "Good afternoon"
        : "Good evening";

  const [heroItem, ...restItems] = OP_ITEMS;

  return (
    <>
      <style>{`
        @keyframes opCardRise {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes opFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .op-dot-grid {
          background-image:
            radial-gradient(circle, rgba(15,118,110,0.08) 1px, transparent 1px);
          background-size: 30px 30px;
        }
      `}</style>

      <div className="op-dot-grid flex min-h-screen flex-col bg-slate-50">
        <div
          style={{ animation: "opFadeIn 0.5s ease both" }}
          className="mx-auto w-full max-w-screen-xl px-6 pb-6 pt-10 sm:px-10"
        >
          <div className="mb-4 flex items-center gap-2">
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-600" />
            <span className="text-xs font-bold uppercase tracking-widest text-teal-700">
              MOI NOTE &middot; Enterprise Portal
            </span>
          </div>

          <h1 className="text-4xl font-black leading-none tracking-tight text-slate-900 sm:text-5xl">
            {greeting}
            {orgName ? (
              <span className="text-teal-700">, {orgName}</span>
            ) : (
              <span className="text-teal-700"> Enterprise</span>
            )}
          </h1>

          <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-500">
            Manage events, customers, and contributions from your enterprise
            dashboard.
          </p>

          <div className="mt-6 h-0.5 w-16 rounded-full bg-gradient-to-r from-teal-600 via-sky-500 to-transparent" />
        </div>

        <div className="mx-auto flex-1 w-full max-w-screen-xl space-y-4 px-6 pb-12 sm:px-10">
          <OpCard item={heroItem} index={0} hero={true} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {restItems.slice(0, 3).map((item, i) => (
              <OpCard key={item.to} item={item} index={i + 1} hero={false} />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {restItems.slice(3).map((item, i) => (
              <OpCard key={item.to} item={item} index={i + 4} hero={false} />
            ))}
          </div>

          <p
            style={{ animation: "opFadeIn 0.5s 0.7s ease both", opacity: 0 }}
            className="pt-4 text-center text-xs text-slate-700"
          >
            MOI NOTE &middot; Enterprise Dashboard &middot; Digital Moi Tech
          </p>
        </div>
      </div>
    </>
  );
}

export default OperatorHome;
